// src/components/Inquiry/items/inquiryItemColumns.ts
//
// One grid for every kind of inquiry line, in both modes.
//
// InquiryItemsTable stacks several row variants in a single <TableBody>, and
// each of them used to choose its own cells: a machined part rendered four
// columns with the quantity last, a custom line rendered five with the
// quantity first, and a converted line rendered two with a colSpan={4}. The
// editable table went further and was a second table entirely, with its own
// column list and its own CSS module. Read together, the lines of one inquiry
// looked like three tables.
//
// Every row now fills these four cells, in this order, always - a variant with
// nothing to say in a column says so with EMPTY rather than by collapsing the
// cell into its neighbour. Edit mode adds one leading cell, and only that;
// read mode from Pickup / Delivery on appends the goods-in cells (goodsIn.tsx).
import { TableCellProps } from "@mui/material";
import { SP_1, SP_2 } from "components/Dashboard/dashboardTable";
import { InquiryTypeEnum, isMaterialInquiry } from "types/InquiryTypes";

export { EMPTY } from "./inquiryLine";

type Column = {
  label: string;
  // Spread onto both the header cell and the body cell, so a column cannot
  // drift out of alignment with its own heading.
  cell: { align: TableCellProps["align"]; sx: { width: string } };
};

// Every cell in the table is laid out the same way; only the column changes.
export const itemCellProps = (
  column: Column,
  extraSx: Record<string, unknown> = {}
) => ({
  align: column.cell.align,
  sx: { ...column.cell.sx, padding: `${SP_1} ${SP_2}`, ...extraSx },
});

export const INQUIRY_ITEM_COLUMNS: Record<
  "item" | "spec" | "files" | "qty",
  Column
> = {
  // What the line is: a preview, the product id, its name.
  item: { label: "Item", cell: { align: "left", sx: { width: "32%" } } },
  // What it is made of / what was asked for. Material and dimensions on a
  // machined part, the description on a custom line, the requisition spec on
  // a converted one.
  spec: { label: "Spec", cell: { align: "left", sx: { width: "38%" } } },
  files: { label: "Files", cell: { align: "center", sx: { width: "18%" } } },
  qty: { label: "Qty", cell: { align: "right", sx: { width: "12%" } } },
};

// A material line is a piece of stock described by its material and its
// measurements; the piece count is the requisition's business, and next to
// the dimensions it read as a second, conflicting size. So a material inquiry
// has no Qty column, in either mode - every row and the header ask this, and a
// row that drew the cell anyway would push its neighbours out of line. The
// goods-in counts still run against the line's quantity and show it as their
// total.
export const showsQuantity = (inquiryType?: InquiryTypeEnum) =>
  !isMaterialInquiry(inquiryType);

// Edit mode only: the tick that puts a line on the inquiry, and the bin that
// takes a free-text line off it. Kept out of INQUIRY_ITEM_COLUMNS so the
// read-only header cannot grow a column it has no cells for.
export const SELECT_COLUMN: Column = {
  label: "",
  cell: { align: "center", sx: { width: "6%" } },
};
